import React from 'react';
import { motion } from 'framer-motion';
import { CheckCircle2 } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';

const TintingOptions = () => {
  const tintOptions = [
    {
      name: 'Carbon Tint',
      description: 'Carbon window film is infused with carbon particles, providing a rich, matte-black finish that won\'t fade over time. It offers superior heat rejection compared to standard dyed films.',
      features: [
        'Rich matte-black finish',
        'No signal interference',
        'Better heat rejection than dyed films',
        'Will not fade or turn purple'
      ]
    },
    {
      name: 'Ceramic Tint',
      description: 'Ceramic window tint represents the pinnacle of tinting technology. Using non-conductive ceramic particles, it blocks up to 99% of UV rays and offers maximum infrared heat rejection.',
      features: [
        'Maximum heat rejection (Infrared)',
        'Superior clarity & visibility',
        'Blocks 99% of UV rays',
        'Non-metallic, no signal interference'
      ],
      recommended: true
    }
  ];

  return (
    <section id="options" className="py-20 bg-gradient-to-b from-black via-gray-900 to-black relative overflow-hidden">
      <div className="absolute inset-0 opacity-10">
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-gold-600/30 rounded-full blur-3xl"></div>
      </div>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-100px' }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <span className="text-gold-500 font-semibold text-sm uppercase tracking-wider">Premium Films</span>
          <h2 className="text-4xl md:text-5xl font-bold text-white mt-4 mb-6">Tinting Options</h2>
          <p className="text-gray-400 text-lg max-w-2xl mx-auto">
            Choose the perfect film type for your needs. All options include lifetime warranty and professional installation.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 gap-8 mb-20">
          {tintOptions.map((option, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-50px' }}
              transition={{ duration: 0.5, delay: index * 0.2 }}
            >
              <Card className={`bg-gray-800/30 border-gray-700 hover:border-gold-600/50 transition-all duration-300 relative overflow-hidden h-full ${
                option.recommended ? 'ring-2 ring-gold-600' : ''
              }`}>
                {option.recommended && (
                  <div className="absolute top-4 right-4 bg-gold-500 text-black px-3 py-1 rounded-full text-xs font-bold">
                    RECOMMENDED
                  </div>
                )}
                <CardHeader>
                  <CardTitle className="text-2xl text-white">{option.name}</CardTitle>
                  <CardDescription className="text-gray-400 mt-2">{option.description}</CardDescription>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-3">
                    {option.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start space-x-3">
                        <CheckCircle2 className="w-5 h-5 text-gold-500 mt-0.5 flex-shrink-0" />
                        <span className="text-gray-300">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default TintingOptions;
